import { useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import {
  HITLAB_SESSION_MESSAGE,
  buildHitLabAuthConfig,
  getHitLabApiMessage,
  getHitLabAttribution,
  isHitLabClientEnabled,
} from './hitLabUtils';
import './hitLab.css';

const INITIAL_PROFILE_FORM = {
  artistName: '',
  cityRegion: '',
  contactEmail: '',
  contactPhone: '',
  songTitle: '',
  genreLane: '',
  releaseStatus: '',
  bestContactOrSocialUrl: '',
  whyThisSong: '',
  hookTimestamp: '',
  cleanExplicit: '',
};

const REQUIRED_PROFILE_FIELDS = [
  'artistName',
  'cityRegion',
  'contactEmail',
  'contactPhone',
  'songTitle',
  'genreLane',
];

const PROFILE_LABELS = {
  artistName: 'Artist name',
  cityRegion: 'City / region',
  contactEmail: 'Contact email',
  contactPhone: 'Phone number',
  songTitle: 'Song title',
  genreLane: 'Genre lane',
  releaseStatus: 'Release status',
  bestContactOrSocialUrl: 'Best contact or social URL',
  whyThisSong: 'Why this song',
  hookTimestamp: 'Hook timestamp',
  cleanExplicit: 'Clean / explicit',
};

const PROFILE_LIMITS = {
  artistName: 120,
  cityRegion: 120,
  contactEmail: 254,
  contactPhone: 40,
  songTitle: 160,
  genreLane: 80,
  releaseStatus: 80,
  bestContactOrSocialUrl: 500,
  whyThisSong: 1000,
  hookTimestamp: 32,
  cleanExplicit: 40,
};

const isValidEmail = (value) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || '').trim());

const trimProfileForm = (form) =>
  Object.keys(INITIAL_PROFILE_FORM).reduce((next, key) => {
    const value = String(form[key] || '').trim();
    next[key] = key === 'contactEmail' ? value.toLowerCase() : value;
    return next;
  }, {});

const getProfileFromIntake = (intake = {}) =>
  Object.keys(INITIAL_PROFILE_FORM).reduce((next, key) => {
    next[key] = intake?.[key] || '';
    return next;
  }, {});

export default function HitLabIntake() {
  const { player } = useContext(AuthContext);
  const location = useLocation();
  const attribution = useMemo(
    () => getHitLabAttribution(location.search),
    [location.search],
  );
  const authConfig = useMemo(
    () => buildHitLabAuthConfig(player),
    [player?.token],
  );
  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState(null);
  const [profileForm, setProfileForm] = useState(INITIAL_PROFILE_FORM);
  const [payment, setPayment] = useState({
    amountCents: 999,
    currency: 'usd',
    priceDisplay: '$9.99',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [stage, setStage] = useState('profile');
  const clientEnabled = isHitLabClientEnabled();
  const query = useMemo(
    () => new URLSearchParams(location.search || ''),
    [location.search],
  );

  useEffect(() => {
    if (!clientEnabled) {
      setLoading(false);
      return undefined;
    }
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      setLoading(false);
      return undefined;
    }
    let active = true;
    axiosConn
      .get('hitlab/intake/me', authConfig)
      .then((response) => {
        if (!active) return;
        const nextIntake = response.data?.data?.intake || null;
        setIntake(nextIntake);
        setProfileForm((current) => ({
          ...current,
          ...getProfileFromIntake(nextIntake),
        }));
        if (response.data?.data?.payment) {
          setPayment(response.data.data.payment);
        }
      })
      .catch((err) => {
        if (active) {
          setError(
            getHitLabApiMessage(
              err,
              'Artist intake status could not be loaded. Please try again.',
            ),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authConfig, clientEnabled]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setProfileForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const trimmedProfile = useMemo(
    () => trimProfileForm(profileForm),
    [profileForm],
  );
  const missingRequired = REQUIRED_PROFILE_FIELDS.filter(
    (key) => !trimmedProfile[key],
  );
  const emailReady = isValidEmail(trimmedProfile.contactEmail);
  const profileReady = missingRequired.length === 0 && emailReady;
  const showPaymentStep = stage === 'payment' && profileReady;
  const paid =
    intake?.paymentStatus === 'paid' || intake?.status === 'paid_pending_upload';
  const checkoutBlocked =
    intake &&
    !paid &&
    intake.status &&
    intake.status !== 'pending_payment';

  const continueToPayment = (event) => {
    event.preventDefault();
    setAttemptedNext(true);
    setError('');
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!profileReady) {
      setError('Complete the required artist profile fields before continuing.');
      return;
    }
    setStage('payment');
  };

  const startCheckout = async () => {
    setError('');
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!profileReady) {
      setStage('profile');
      setAttemptedNext(true);
      setError('Complete the required artist profile fields before continuing.');
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const response = await axiosConn.post(
        'hitlab/intake/checkout',
        {
          attribution,
          profileDraft: trimmedProfile,
        },
        authConfig,
      );
      const payload = response.data?.data || {};
      if (payload.intake) setIntake(payload.intake);
      if (payload.payment) setPayment(payload.payment);
      if (payload.checkoutUrl) {
        window.location.assign(payload.checkoutUrl);
        return;
      }
      if (
        payload.alreadyPaid ||
        payload.intake?.paymentStatus === 'paid' ||
        payload.intake?.status === 'paid_pending_upload'
      ) {
        setBusy(false);
        return;
      }
      throw new Error('missing-checkout-url');
    } catch (err) {
      setError(
        getHitLabApiMessage(
          err,
          'Checkout could not be started. Please try again.',
        ),
      );
      setBusy(false);
    }
  };

  const renderField = (name, { textarea = false, required = false, type = 'text', placeholder = '' } = {}) => {
    const inputProps = {
      name,
      value: profileForm[name],
      onChange: updateField,
      maxLength: PROFILE_LIMITS[name],
      required,
      placeholder,
    };
    return (
      <label className={textarea ? 'hitlab-form-wide' : undefined}>
        {PROFILE_LABELS[name]}
        {textarea ? (
          <textarea {...inputProps} />
        ) : (
          <input type={type} {...inputProps} />
        )}
      </label>
    );
  };

  return (
    <main className="hitlab-page hitlab-workflow">
      <Helmet>
        <title>JG HitLab Intake | Artist Profile</title>
      </Helmet>
      <section className="hitlab-shell hitlab-workflow-grid">
        <div className="hitlab-workflow-copy">
          <p className="hitlab-kicker">ARTIST PROFILE</p>
          <h1>Create Your JG Artist Profile</h1>
          <p className="hitlab-lede">
            Tell us who is submitting and what record is entering the queue.
            This first step creates your JG artist profile and song snapshot.
          </p>

          {query.get('checkout') === 'cancel' ? (
            <div className="hitlab-alert">Checkout was canceled. Your profile is saved; you can continue when ready.</div>
          ) : null}
          {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
          {!clientEnabled ? (
            <div className="hitlab-alert">Artist intake is not accepting new submissions right now.</div>
          ) : loading ? (
            <div className="hitlab-alert">Loading artist profile...</div>
          ) : !authConfig ? (
            null
          ) : paid ? (
            <Link className="hitlab-primary-link" to="/hitlab/submit">
              Continue to song submission
            </Link>
          ) : checkoutBlocked ? (
            <div className="hitlab-alert">
              Your artist intake is already in progress. Current status: {intake?.status || 'in review'}.
            </div>
          ) : showPaymentStep ? (
            <section className="hitlab-form hitlab-payment-stage" aria-label="Artist intake processing">
              <p className="hitlab-kicker">ARTIST INTAKE PROCESSING</p>
              <h2>Your Artist Profile Is Ready</h2>
              <p className="hitlab-lede">
                The next step is the one-time intake processing payment.
              </p>
              <div className="hitlab-price-block">
                {payment.priceDisplay || '$9.99'} One-Time Processing Fee
              </div>
              <p>
                Payment gets your song processed. Excellence gets your song played.
              </p>
              <p>
                Processing covers file handling, rights screening, review queue
                entry, and curator listening. It does not buy approval,
                placement, rotation, positive feedback, Hit Radar, public
                promotion, or any outcome.
              </p>
              <div className="hitlab-stage-actions">
                <button
                  type="button"
                  className="hitlab-secondary-btn"
                  onClick={() => setStage('profile')}
                >
                  Edit artist profile
                </button>
                <button
                  type="button"
                  className="hitlab-primary-btn"
                  disabled={busy}
                  onClick={startCheckout}
                >
                  {busy
                    ? 'Opening secure payment...'
                    : `Continue to Secure Payment - ${payment.priceDisplay || '$9.99'}`}
                </button>
              </div>
            </section>
          ) : (
            <form className="hitlab-form hitlab-profile-form" onSubmit={continueToPayment} noValidate>
              <div className="hitlab-form-grid">
                {renderField('artistName', { required: true })}
                {renderField('cityRegion', { required: true })}
                {renderField('contactEmail', { required: true, type: 'email' })}
                {renderField('contactPhone', { required: true })}
                {renderField('songTitle', { required: true })}
                {renderField('genreLane', { required: true })}
                {renderField('releaseStatus', {
                  placeholder: 'Unreleased, released, demo, mastered',
                })}
                {renderField('bestContactOrSocialUrl', {
                  placeholder: 'https://instagram.com/artist',
                })}
                {renderField('hookTimestamp', { placeholder: '0:42' })}
                {renderField('cleanExplicit')}
                {renderField('whyThisSong', { textarea: true })}
              </div>

              {attemptedNext && !profileReady ? (
                <div className="hitlab-alert hitlab-alert-error">
                  Required fields: artist name, city / region, contact email,
                  phone number, song title, and genre lane.
                </div>
              ) : null}

              <button type="submit" className="hitlab-primary-btn">
                Next
              </button>
            </form>
          )}
        </div>
        <aside className={`hitlab-checkout-panel ${showPaymentStep ? 'hitlab-checkout-panel-active' : ''}`}>
          <span>{showPaymentStep ? 'PROCESSING BOUNDARY' : 'PROFILE FIRST'}</span>
          {showPaymentStep ? (
            <>
              <h2>Processing, not placement.</h2>
              <p className="hitlab-panel-copy">
                Payment gets your song processed. Excellence gets your song played.
              </p>
              <p className="hitlab-panel-copy">
                Processing covers file handling, rights screening, review queue
                entry, and curator listening. It does not buy approval,
                placement, rotation, positive feedback, Hit Radar, public
                promotion, or any outcome.
              </p>
            </>
          ) : (
            <>
              <h2>Build the snapshot first.</h2>
              <p className="hitlab-panel-copy">
                Next unlocks only after the required artist profile fields are
                complete.
              </p>
              <ul>
                <li>Artist identity</li>
                <li>City / region</li>
                <li>Contact email</li>
                <li>Phone number</li>
                <li>Song title</li>
                <li>Genre lane</li>
              </ul>
            </>
          )}
        </aside>
      </section>
    </main>
  );
}
